import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';

const ToastContext = createContext(null);

let globalAddToast = null;

/**
 * Show a toast notification from anywhere (no hook needed).
 * @param {string} message - Toast message
 * @param {'success'|'error'|'info'} [type='info'] - Toast type
 * @param {number} [duration=4000] - Duration in ms
 */
export function toast(message, type = 'info', duration = 4000) {
  if (globalAddToast) {
    globalAddToast(message, type, duration);
  } else {
    // Fallback if ToastProvider not mounted
    console.warn('[Toast] ToastProvider not mounted, falling back to console:', message);
  }
}

toast.success = (msg, duration) => toast(msg, 'success', duration);
toast.error = (msg, duration) => toast(msg, 'error', duration);
toast.info = (msg, duration) => toast(msg, 'info', duration);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type, duration }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Register global function
  useEffect(() => {
    globalAddToast = addToast;
    return () => { globalAddToast = null; };
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      <div style={containerStyle}>
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

function ToastItem({ toast: t, onRemove }) {
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const fadeTimer = setTimeout(() => setExiting(true), t.duration - 300);
    const removeTimer = setTimeout(() => onRemove(t.id), t.duration);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(removeTimer);
    };
  }, [t.id, t.duration, onRemove]);

  const colors = {
    success: { bg: '#059669', icon: '\u2705' },
    error:   { bg: '#dc2626', icon: '\u274c' },
    info:    { bg: '#2563eb', icon: '\u2139\ufe0f' },
  };

  const c = colors[t.type] || colors.info;

  return (
    <div
      style={{
        ...toastStyle,
        backgroundColor: c.bg,
        opacity: exiting ? 0 : 1,
        transform: exiting ? 'translateX(100%)' : 'translateX(0)',
      }}
      onClick={() => onRemove(t.id)}
    >
      <span style={{ marginRight: '8px' }}>{c.icon}</span>
      <span>{t.message}</span>
    </div>
  );
}

const containerStyle = {
  position: 'fixed',
  top: '20px',
  right: '20px',
  zIndex: 10000,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  maxWidth: '400px',
};

const toastStyle = {
  padding: '12px 20px',
  borderRadius: '8px',
  color: 'white',
  fontSize: '14px',
  fontWeight: '500',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  cursor: 'pointer',
  transition: 'opacity 0.3s ease, transform 0.3s ease',
  display: 'flex',
  alignItems: 'center',
  lineHeight: '1.4',
  wordBreak: 'break-word',
};
